/**
 * FleetCarPicker — pick the stock car to put into the rental fleet. Searches the group's read-only vehicle
 * directory by plate or VIN (the same projection the servis/uskladnění pickers use, admitted for půjčovna
 * write); each hit is already the car's canonical live row. A car whose VIN is ambiguous is listed but can't
 * be picked: the server would refuse it, and the cure is a cleanup in the stock, not here.
 */

import { useEffect, useRef, useState } from 'react';
import { Badge } from '@/components/Badge';
import { Button } from '@/components/Button';
import { Field } from '@/components/Field';
import { Pill } from '@/components/Pill';
import { Skeleton } from '@/components/Skeleton';
import { TextInput } from '@/components/TextInput';
import { useDebouncedValue } from '@/lib/use-debounced-value';
import { useFleetCarSearchQuery, type DirectoryVehicle } from './api';

export function carTitle(c: Pick<DirectoryVehicle, 'znacka_nazev' | 'model_nazev' | 'provedeni'>): string {
  return [c.znacka_nazev, c.model_nazev, c.provedeni].filter(Boolean).join(' ') || 'Vozidlo';
}

const norm = (vin: string) => vin.replace(/\s+/g, '').toUpperCase();

interface FleetCarPickerProps {
  value: DirectoryVehicle | null;
  onChange: (car: DirectoryVehicle | null) => void;
  /** A VIN to select as soon as the directory returns exactly that car (a link from a car's detail). */
  initialVin?: string;
  /**
   * VINs already in this firma's fleet: shown, not selectable, never preselected. `null` = the fleet is
   * not known yet; the preselection waits for it, so a deep link to an enrolled car can't slip through.
   */
  enrolledVins: ReadonlySet<string> | null;
  /** The enrolment is being saved: the payload holds the picked car, so it can no longer be changed. */
  disabled?: boolean;
}

export function FleetCarPicker({ value, onChange, initialVin, enrolledVins, disabled = false }: FleetCarPickerProps) {
  const [text, setText] = useState(initialVin ?? '');
  const term = useDebouncedValue(text, 250);
  const search = useFleetCarSearchQuery(term, value == null);
  const preselected = useRef(false);

  // The deep link carries a VIN: pick that car once it shows up (exact VIN match only).
  useEffect(() => {
    if (preselected.current || !initialVin || value || !search.data || !enrolledVins) return;
    const hit = search.data.find(
      (c) => norm(c.vin) === norm(initialVin) && !c.vlastnik_nejasny && !enrolledVins.has(norm(c.vin)),
    );
    if (hit) {
      preselected.current = true;
      onChange(hit);
    }
  }, [initialVin, search.data, value, enrolledVins, onChange]);

  if (value) {
    return (
      <div data-testid="fleet-picked-car" className="flex items-center gap-3 border border-line bg-paper px-4 py-3">
        <div className="min-w-0 flex-1">
          <div className="text-[14px] text-ink">{carTitle(value)}</div>
          <div className="mt-1 flex flex-wrap items-center gap-2">
            {value.spz && <Pill>{value.spz}</Pill>}
            <span className="font-mono text-[12px] text-ink-3">{value.vin}</span>
            {value.vlastnik_nazev && <span className="text-[12px] text-ink-3">{value.vlastnik_nazev}</span>}
          </div>
        </div>
        <Button variant="secondary" size="sm" onClick={() => onChange(null)} disabled={disabled}>
          Změnit vozidlo
        </Button>
      </div>
    );
  }

  const rows = search.data ?? [];
  const searching = text.trim().length >= 2;

  return (
    <div className="flex flex-col gap-3">
      <Field label="Vozidlo" required hint="Hledejte podle SPZ nebo VIN mezi vozy vaší firmy a firem ve skupině.">
        <TextInput
          value={text}
          autoComplete="off"
          placeholder="SPZ nebo VIN"
          data-testid="fleet-car-search"
          disabled={disabled}
          onChange={(e) => setText(e.currentTarget.value)}
        />
      </Field>
      {searching && (
        <div data-testid="fleet-car-results" className="border border-line bg-paper">
          {/* Results are not offered before the fleet is known: a hit for an enrolled car would be clickable
              for a moment and the later fleet answer could not take the selection back. */}
          {search.isLoading || term !== text || enrolledVins === null ? (
            <div className="flex flex-col gap-2 p-3">
              <Skeleton className="h-9 w-full" />
              <Skeleton className="h-9 w-full" />
            </div>
          ) : search.isError ? (
            <p className="px-4 py-3 text-[13px] text-ink-2">Vozidla se nepodařilo načíst.</p>
          ) : rows.length === 0 ? (
            <p className="px-4 py-3 text-[13px] text-ink-2">Nic nenalezeno. Vozidlo musí být ve skladu vaší firmy nebo firmy ve skupině.</p>
          ) : (
            <ul>
              {rows.map((c) => {
                const enrolled = enrolledVins?.has(norm(c.vin)) ?? false;
                const blocked = c.vlastnik_nejasny || enrolled;
                return (
                  <li key={`${c.kind}:${c.id_vozidla}`} className="border-b border-line last:border-b-0">
                    <button
                      type="button"
                      disabled={blocked || disabled}
                      onClick={() => onChange(c)}
                      className="flex w-full items-center gap-3 px-4 py-3 text-left hover:bg-accent-soft disabled:cursor-not-allowed disabled:opacity-60 disabled:hover:bg-transparent"
                    >
                      <span className="min-w-0 flex-1">
                        <span className="block text-[14px] text-ink">{carTitle(c)}</span>
                        <span className="mt-1 flex flex-wrap items-center gap-2">
                          {c.spz && <Pill>{c.spz}</Pill>}
                          <span className="font-mono text-[12px] text-ink-3">{c.vin}</span>
                          {c.vlastnik_nazev && <span className="text-[12px] text-ink-3">{c.vlastnik_nazev}</span>}
                        </span>
                      </span>
                      {c.vlastnik_nejasny ? (
                        <Badge tone="warn">Duplicitní VIN</Badge>
                      ) : enrolled ? (
                        <Badge tone="neutral">Už v půjčovně</Badge>
                      ) : (
                        <Badge tone="neutral">{c.kind === 'nove' ? 'Nové' : 'Ojeté'}</Badge>
                      )}
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}
